import { get } from "@/services/axiosUitils";
import { getLocalDateString } from "../utils/homePageUtils";

export interface TrendsGraphPoint {
  day: string;
  value: number;
}

export interface TrendsWeeklyBlock {
  graph?: TrendsGraphPoint[];
  points?: TrendsGraphPoint[];
  this_week?: Array<Record<string, unknown>>;
  daily_avg?: number;
  weekly_average?: number;
  weekly_total?: number;
  goal?: number;
  current?: number;
  value?: number;
  unit?: string;
  start_date?: string;
  end_date?: string;
  today?: {
    date?: string;
    day?: string;
    day_full?: string;
  };
  days?: Array<Record<string, unknown>>;
}

export interface TrendsGlucoseCurvePoint {
  phase?: string;
  time_min: number;
  glucose_mg_dl: number;
  total_rise?: number;
  new_meal_rise?: number;
  prev_meal_remaining?: number;
}

export interface TrendsGlucoseGraph {
  start_mg_dl?: number;
  peak_mg_dl?: number;
  spike_mg_dl?: number;
  time_to_peak_min?: number;
  recovery_time_min?: number;
  confidence?: string;
  spike_category?: string;
  spike_range?: { low: number; high: number };
  spike_threshold?: number;
  color?: string;
  title?: string;
  ui_message?: string;
  curve?: {
    points: TrendsGlucoseCurvePoint[];
    is_cumulative?: boolean;
    starting_glucose?: number;
  };
}

export interface TrendsLatestMeal {
  meal_id?: string;
  meal_type?: string;
  meal_title?: string;
  meal_time_local?: string;
  meal_time?: string;
  meal_time_display?: string;
}

export interface TrendsWeight {
  weight_kg?: number | null;
  weight_lbs?: number | null;
  unit?: string;
}

export interface TrendsBbsGraphPoint {
  date: string;
  day: string;
  day_full?: string;
  value: number | null;
  meals_count?: number;
}

export interface TrendsBbsTrend {
  start_date?: string;
  end_date?: string;
  today?: {
    date?: string;
    day?: string;
    day_full?: string;
  };
  days?: Array<{
    date?: string;
    day?: string;
    is_today?: boolean;
    value?: number | null;
    daily_bbs?: number | null;
    bbs_label?: string | null;
    bbs_provisional?: boolean | null;
    meals_count?: number;
  }>;
  graph?: TrendsBbsGraphPoint[];
}

/** Response from GET /food_scanner/trends/ */
export interface TrendsApiResponse {
  success?: { code: string; message: string };
  has_data?: boolean;
  date_local?: string;
  today_date_local?: string;
  weight?: TrendsWeight;
  latest_meal?: TrendsLatestMeal;
  graph?: TrendsGlucoseGraph;
  bbs_trend?: TrendsBbsTrend;

  // Legacy / future fitness fields
  today_overview?: Record<string, unknown>;
  "Today's Overview"?: Record<string, unknown>;
  "Today's Activity"?: Record<string, unknown>;
  progress?: {
    weekly_steps?: TrendsWeeklyBlock;
    weekly_calories?: TrendsWeeklyBlock;
    weekly_calories_burned?: TrendsWeeklyBlock;
    weekly_sleep?: TrendsWeeklyBlock;
  };
  learning?: {
    hrv?: TrendsWeeklyBlock & { current?: number; current_value?: number };
    vitals?: Record<string, unknown>;
  };
  nutrition?: Record<string, unknown>;
  care?: Record<string, unknown>;
  weekly_steps?: TrendsWeeklyBlock;
  weekly_calories_burned?: TrendsWeeklyBlock;
  weekly_sleep?: TrendsWeeklyBlock;
  "Weekly Calorie Burned Trend"?: { graph?: TrendsGraphPoint[] };
  "Weekly Sleep Trend"?: { graph?: TrendsGraphPoint[] };
  glucose_spike_trend?: TrendsWeeklyBlock;
  steps?: number;
  steps_goal?: number;
  calories_burned?: number;
  calories_goal?: number;
  active_zone_minutes?: number;
  active_zone_minutes_goal?: number;
  resting_heart_rate?: number;
  sleep_minutes?: number;
  sleep_hours?: string;
  sleep_goal?: number;
  weight_kg?: number | null;
  weight_lbs?: number | null;
  preferred_weight_unit?: string;
  distance_km?: number;
  /** Some APIs send km as `distance`. */
  distance?: number;
  preferred_distance_unit?: string;
  hrv_rmssd?: number;
  spo2_percent?: number;
  breathing_rate?: number;
  skin_temp_celsius?: number;
  "Bite Balance Score"?: { daily_bbs?: number; label?: string };
  glucose_spike?: {
    this_meal_spike_mg_dl?: number;
    predicted_spike_mg_dl?: number;
  };
  [key: string]: unknown;
}

class TrendsService {
  private static instance: TrendsService;

  private constructor() {}

  public static getInstance(): TrendsService {
    if (!TrendsService.instance) {
      TrendsService.instance = new TrendsService();
    }
    return TrendsService.instance;
  }

  async getTrends(date?: string, config?: { showNotification?: boolean }): Promise<TrendsApiResponse | null> {
    const requestDate = date || getLocalDateString();
    const url = `/food_scanner/trends/?date=${encodeURIComponent(requestDate)}`;
    const response = await get<TrendsApiResponse>(url, {
      showNotification: false,
      headers: {
        "X-User-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      ...config,
    });
    if (response === undefined || response === null) return null;
    return response;
  }
}

export default TrendsService.getInstance();
